/**
 * Create Task dialog — opens the same Task Editor as edit (UnifiedTaskEditor in create mode).
 * Same UI as when editing a task; only difference is empty fields and save creates the task.
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';
import { UnifiedTaskEditor } from './UnifiedTaskEditor';
import type { TaskStatus } from '@/types';

interface CreateTaskDialogProps {
  projectId: string;
  listId: string;
  statuses: TaskStatus[];
  projectName?: string;
  listName?: string;
}

export function CreateTaskDialog({
  projectId,
  listId,
  statuses,
  projectName,
  listName,
}: CreateTaskDialogProps) {
  const { createTaskOpen, createTaskPreselectedStatusId, closeCreateTask } = useUIStore();

  if (!createTaskOpen) return null;

  return (
    <DialogPrimitive.Root open={createTaskOpen} onOpenChange={(open) => !open && closeCreateTask()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-50 flex flex-col',
            'bg-[#1a1a1a]',
            'overflow-hidden'
          )}
        >
          <div className="flex-1 overflow-hidden min-h-0">
            <UnifiedTaskEditor
              mode="create"
              projectId={projectId}
              listId={listId}
              statuses={statuses}
              preselectedStatusId={createTaskPreselectedStatusId ?? undefined}
              projectName={projectName}
              listName={listName}
              onClose={closeCreateTask}
              onCreated={() => closeCreateTask()}
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
